import * as React from "react";
import { AiStatusPill } from "./components/ui/AiStatusPill";
import { categoryMeta } from "./tokens";
import { AnalysisView } from "./AnalysisView";
import { SuggestionView } from "./SuggestionView";
import { IconChevronLeft } from "./icons";
import { ruleTitle } from "./utils";
import type { DetailTab, SelectedIssue } from "./types";

export type IssueDetailProps = {
  selected: SelectedIssue;
  activeTab: DetailTab;
  status: "pending" | "accepted" | "rejected";
  isRetrying: boolean;
  retryError?: string;
  busy: boolean;
  isEditing: boolean;
  hasAppliedEdit: boolean;
  draftText: string;
  displayOverride: string | null;
  tabsDisabled: boolean;
  onClose: () => void;
  onTabChange: (tab: DetailTab) => void;
  onDraftChange: (text: string) => void;
  onEnterEdit: () => void;
  onApplyEdit: () => void;
  onCancelEdit: () => void;
  onRetry: () => void;
  onClearRetryError?: () => void;
  onAccept: () => void;
  onReject: () => void;
  onEditKeyDown?: (e: React.KeyboardEvent) => void;
  onDetailKeyDown?: (e: React.KeyboardEvent) => void;
  pulseHighlight?: boolean;
};

export const IssueDetail: React.FC<IssueDetailProps> = ({
  selected,
  activeTab,
  status,
  isRetrying,
  retryError,
  busy,
  isEditing,
  hasAppliedEdit,
  draftText,
  displayOverride,
  tabsDisabled,
  onClose,
  onTabChange,
  onDraftChange,
  onEnterEdit,
  onApplyEdit,
  onCancelEdit,
  onRetry,
  onClearRetryError,
  onAccept,
  onReject,
  onEditKeyDown,
  onDetailKeyDown,
  pulseHighlight,
}) => {
  const { row, category, positionInCategory, totalInCategory } = selected;
  const meta = categoryMeta[category];
  const isCompliant = category === "compliant";
  const showSuggestionTab = !isCompliant;

  const handleTabKeyDown = (e: React.KeyboardEvent, tab: DetailTab) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      if (!showSuggestionTab) return;
      onTabChange(tab === "analysis" ? "suggestion" : "analysis");
    }
  };

  return (
    <div className="ai-analysis__detail" onKeyDown={onDetailKeyDown} tabIndex={-1}>
      <button type="button" className="ai-analysis__breadcrumb" onClick={onClose}>
        <IconChevronLeft size={12} />
        <span>
          {meta.label}
          <span className="ai-analysis__muted" style={{ margin: "0 6px" }}>
            ·
          </span>
          {positionInCategory} of {totalInCategory}
          <span className="ai-analysis__muted" style={{ margin: "0 6px" }}>
            ·
          </span>
          ¶{row.index}
        </span>
      </button>

      <div className="ai-analysis__detail-header">
        <h2 className="ai-analysis__detail-title">{ruleTitle(row)}</h2>
        {!isCompliant && (status === "accepted" || status === "rejected") ? (
          <AiStatusPill variant={status} />
        ) : null}
      </div>

      {!isEditing && (showSuggestionTab || !isCompliant) ? (
        <div className="ai-analysis__tabs" role="tablist" aria-label="Issue detail">
          {showSuggestionTab ? (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "suggestion"}
              className={`ai-analysis__tab${activeTab === "suggestion" ? " ai-analysis__tab--active" : ""}`}
              disabled={tabsDisabled}
              onClick={() => onTabChange("suggestion")}
              onKeyDown={(e) => handleTabKeyDown(e, "suggestion")}
            >
              Suggestion
              {hasAppliedEdit ? <AiStatusPill variant="edited" /> : null}
            </button>
          ) : null}
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "analysis"}
            className={`ai-analysis__tab${activeTab === "analysis" ? " ai-analysis__tab--active" : ""}`}
            disabled={tabsDisabled}
            onClick={() => onTabChange("analysis")}
            onKeyDown={(e) => handleTabKeyDown(e, "analysis")}
          >
            Analysis
          </button>
        </div>
      ) : null}

      <div className="ai-analysis__tab-body" role="tabpanel">
        {isEditing || (activeTab === "suggestion" && showSuggestionTab) ? (
          <SuggestionView
            row={row}
            status={status}
            isRetrying={isRetrying}
            retryError={retryError}
            busy={busy}
            isEditing={isEditing}
            draftText={draftText}
            displayOverride={displayOverride}
            onDraftChange={onDraftChange}
            onEnterEdit={onEnterEdit}
            onApplyEdit={onApplyEdit}
            onCancelEdit={onCancelEdit}
            onRetry={onRetry}
            onClearRetryError={onClearRetryError}
            onAccept={onAccept}
            onReject={onReject}
            onEditKeyDown={onEditKeyDown}
            pulseHighlight={pulseHighlight}
          />
        ) : (
          <AnalysisView row={row} />
        )}
      </div>
    </div>
  );
};
